<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'
import { VTextField } from '@/modules/shared/components'
import { onClickOutside } from '@vueuse/core'
import {
  useAddressAutocomplete,
  type AddressSuggestion,
  type StructuredAddress,
} from '@/modules/shared/composables/use-address-autocomplete'

const props = defineProps<{
  modelValue?: string
  label?: string
  placeholder?: string
  property?: string
  v$?: any
  countryCode?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  select: [address: StructuredAddress]
}>()

const { suggestions, loading, suggest, retrieve, clear } = useAddressAutocomplete()

const open = ref(false)
const activeIndex = ref(-1)
const retrieving = ref(false)
const containerRef = ref<HTMLElement | null>(null)

onClickOutside(containerRef, () => {
  open.value = false
})

function onInput() {
  const val = props.modelValue || ''
  activeIndex.value = -1
  if (val.length >= 3) {
    open.value = true
    suggest(val, props.countryCode)
  } else {
    open.value = false
    clear()
  }
}

function onUpdateModelValue(value: string) {
  emit('update:modelValue', value)
}

async function onSelectSuggestion(suggestion: AddressSuggestion) {
  retrieving.value = true
  open.value = false

  const address = await retrieve(suggestion.mapboxId)
  retrieving.value = false

  if (address) {
    clear()
    emit('select', address)
  }
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value || suggestions.value.length === 0) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      activeIndex.value = Math.min(activeIndex.value + 1, suggestions.value.length - 1)
      break
    case 'ArrowUp':
      e.preventDefault()
      activeIndex.value = Math.max(activeIndex.value - 1, 0)
      break
    case 'Enter':
      e.preventDefault()
      if (activeIndex.value >= 0) {
        onSelectSuggestion(suggestions.value[activeIndex.value])
      }
      break
    case 'Escape':
      open.value = false
      activeIndex.value = -1
      break
  }
}

onBeforeUnmount(() => {
  clear()
})
</script>

<template>
  <div ref="containerRef" class="relative">
    <VTextField
      :modelValue="modelValue"
      @update:modelValue="onUpdateModelValue"
      :label="label"
      :placeholder="placeholder"
      :property="property"
      :v$="v$"
      @input="onInput"
      @keydown="onKeydown"
    />
    <div v-if="loading || retrieving" class="pointer-events-none absolute right-3 top-9 flex items-center">
      <svg
        class="h-4 w-4 animate-spin text-gray-400"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
    <ul
      v-if="open && suggestions.length > 0"
      class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
      role="listbox"
    >
      <li
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.mapboxId"
        class="cursor-pointer select-none px-3 py-2 text-sm"
        :class="index === activeIndex ? 'bg-sky-50 text-sky-900' : 'text-gray-700 hover:bg-gray-50'"
        role="option"
        :aria-selected="index === activeIndex"
        @click="onSelectSuggestion(suggestion)"
        @mouseenter="activeIndex = index"
      >
        <div class="font-medium">{{ suggestion.label }}</div>
        <div v-if="suggestion.description" class="text-xs text-gray-500">{{ suggestion.description }}</div>
      </li>
    </ul>
  </div>
</template>
